/**
 * Equipment-based skill offer eligibility (dungeon entry snapshot).
 *
 * Offer filtering uses the run's equipment snapshot. Skills already assigned
 * to dice remain usable if the player later changes hub inventory — dungeon
 * loadout is snapshotted at entry and does not hot-swap mid-run.
 */

import { getItemDef, type WeaponFamily } from '../data/equipment';
import type { EquipmentSnapshot } from './equipmentCombat';
import type { SkillDefinition } from './types';

export interface WeaponConfig {
  mainHandFamily: WeaponFamily | null;
  mainHandTwoHanded: boolean;
  offHandWeapon: boolean;
  offHandShield: boolean;
  dualWielding: boolean;
}

export function weaponConfigFromSnapshot(
  snapshot: EquipmentSnapshot | null | undefined,
): WeaponConfig {
  if (!snapshot) {
    return {
      mainHandFamily: null,
      mainHandTwoHanded: false,
      offHandWeapon: false,
      offHandShield: false,
      dualWielding: false,
    };
  }
  const mainId = snapshot.equipped.main_hand;
  const offId = snapshot.equipped.off_hand;
  const main = mainId
    ? snapshot.items.find((i) => i.instanceId === mainId)
    : undefined;
  const off = offId
    ? snapshot.items.find((i) => i.instanceId === offId)
    : undefined;
  const mainDef = main ? getItemDef(main.defId) : undefined;
  const offDef = off ? getItemDef(off.defId) : undefined;
  const mainHandFamily = mainDef?.weaponFamily ?? null;
  const mainHandTwoHanded = mainDef?.handedness === 'two_hand';
  const offHandShield = Boolean(offDef?.isShield);
  const offHandWeapon = Boolean(
    offDef &&
      offDef.category === 'weapon' &&
      !offDef.isShield &&
      (offDef.baseWeaponDamage ?? 0) > 0,
  );
  return {
    mainHandFamily,
    mainHandTwoHanded,
    offHandWeapon,
    offHandShield,
    dualWielding: Boolean(
      mainDef &&
        mainDef.handedness === 'one_hand' &&
        offHandWeapon &&
        !mainHandTwoHanded,
    ),
  };
}

export function skillPassesEquipmentGate(
  skill: SkillDefinition,
  config: WeaponConfig,
): boolean {
  const gate = skill.equipmentGate;
  if (!gate) return true;
  if (gate.requireOffHandWeapon && !config.offHandWeapon) return false;
  if (gate.requireDualWield && !config.dualWielding) return false;
  if (gate.requireTwoHand && !config.mainHandTwoHanded) return false;
  if (gate.mainHandFamilies && gate.mainHandFamilies.length > 0) {
    if (
      !config.mainHandFamily ||
      !gate.mainHandFamilies.includes(config.mainHandFamily)
    ) {
      return false;
    }
  }
  return true;
}
